import { updateQueryParam } from "@/lib/url.js";

// The phone board's technician selection, kept in two places: the address,
// so a link reproduces a board, and the browser, so a refresh, a new tab or a
// fresh login lands back on it. Both hold technician profile ids, with
// Unassigned as its pseudo-id -1.

const TECHNICIAN_IDS_PARAM = "technician_ids";

const STORAGE_PREFIX = "schedule-technician-selection";

const toId = (value: unknown): number => {
  if (typeof value === "number") return value;
  // Decimal digits only: Number() would also read "0x7" or "7e0" as an id.
  if (typeof value === "string" && /^\s*-?\d+\s*$/.test(value)) {
    return Number(value);
  }
  return NaN;
};

const toIds = (values: unknown[]): number[] => {
  const ids = values.map(toId).filter(Number.isInteger);
  return [...new Set(ids)];
};

/**
 * Null when the address names no selection at all, which is distinct from a
 * param whose every entry is junk: that is still a link, and the caller treats
 * it as one naming nobody rather than falling through to the saved selection.
 */
export function readTechnicianIdsParam(): number[] | null {
  const raw = new URLSearchParams(window.location.search).get(
    TECHNICIAN_IDS_PARAM
  );
  if (raw === null || raw.trim() === "") return null;
  return toIds(raw.split(","));
}

export function writeTechnicianIdsParam(
  ids: number[],
  { replace = false } = {}
): void {
  updateQueryParam(
    TECHNICIAN_IDS_PARAM,
    ids.length ? ids.join(",") : null,
    replace
  );
}

// Keyed by admin so a shared browser never shows one admin another's board.
// The sys-admin layout sets no user_id, and there is then nobody to key by.
const storageKey = (): string | null => {
  const adminId = (window.currentTenant as { user_id?: string } | undefined)
    ?.user_id;
  return adminId ? `${STORAGE_PREFIX}:${adminId}` : null;
};

// Storage access is wrapped because it can throw outright — a private window,
// blocked site data — and a saved selection is a convenience the board must
// render without.
export function loadSavedTechnicianIds(): number[] | null {
  const key = storageKey();
  if (!key) return null;

  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const saved: unknown = JSON.parse(raw);
    if (!Array.isArray(saved)) return null;
    const ids = toIds(saved);
    return ids.length ? ids : null;
  } catch {
    return null;
  }
}

export function saveTechnicianIds(ids: number[]): void {
  const key = storageKey();
  if (!key) return;

  try {
    if (ids.length) {
      localStorage.setItem(key, JSON.stringify(ids));
    } else {
      localStorage.removeItem(key);
    }
  } catch {
    // Nothing to recover: the board keeps working, it just won't remember.
  }
}
